import { Row } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Avatar } from 'components/atoms/avatar';
import { Form } from 'components/atoms/form';
import { Loading } from 'components/atoms/loading';
import { Modal } from 'components/atoms/modal';
import { Select } from 'components/atoms/select';
import { Caption01 } from 'components/styles/typography';
import { User } from 'data/members/api';
import { usePermissionSetDefinitionLight } from 'queries/rbac';
import { logger } from 'services';
import { getUserLabel } from 'utils/get-user-label';

interface Props {
  user: User | null;
  onSubmit: (roleId: number) => Promise<void>;
  onClose: () => void;
  openCustomerPermissionChange: boolean;
}
const noRoleId = -1;
const Wrapper = styled.div`
  margin-top: var(--spacing-05);
`;

export const StyledRow = styled(Row)`
  width: 100%;
  justify-content: center;
`;

export const MemberRow = styled(Row)`
  width: 100%;
  margin-bottom: var(--spacing-05);
`;

export const AvatarContainer = styled.div`
  margin-right: var(--spacing-03);
`;

const Name = styled.span`
  white-space: nowrap;
  max-width: calc(100% - 32px);
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Text = styled.p`
  margin-bottom: 0;
`;

const MemberRole = styled(Text)`
  ${Caption01};
  color: var(--gray-70);
`;

export const ManagePermissionModal: FC<Props> = ({
  user,
  onSubmit,
  onClose,
  openCustomerPermissionChange,
}) => {
  const { data: permissionWithNames, isLoading } =
    usePermissionSetDefinitionLight();

  const options = useMemo(() => {
    return permissionWithNames
      ?.filter(
        (permission) =>
          permission.permissionSetObjectType === 'PROJECT' &&
          permission.permissionSetSpecificTo ===
            (openCustomerPermissionChange ? 'EXTERNAL' : 'NATIVE') &&
          !permission.default,
      )
      .map((permission) => ({
        value: permission.permissionSetId,
        label: permission.permissionSetName,
      }));
  }, [permissionWithNames, openCustomerPermissionChange]);
  const [selectedPermission, setSelectedPermission] = useState(
    options?.find(
      ({ value }) => value === user?.permissionSet?.permissionSetId,
    ),
  );

  useEffect(() => {
    if (options) {
      setSelectedPermission(
        options?.find(
          ({ value }) => value === user?.permissionSet?.permissionSetId,
        ),
      );
    }
  }, [options, user?.permissionSet?.permissionSetId]);

  const [submitInProgress, setSubmitInProgress] = useState(false);

  const onPermissionChange = useCallback((selected) => {
    setSelectedPermission(selected);
  }, []);

  const onOk = useCallback(async () => {
    setSubmitInProgress(true);
    try {
      await onSubmit(selectedPermission?.value ?? noRoleId);
      onClose();
    } catch (error) {
      logger.error('An error occurred', { error });
    } finally {
      setSubmitInProgress(false);
    }
  }, [onClose, selectedPermission?.value, onSubmit]);

  const memberDetails = useMemo(() => {
    return (
      <MemberRow align="middle">
        <AvatarContainer>
          <Avatar
            src={user?.photoUrl}
            userStatus={user?.userStatus}
            name={user ? getUserLabel(user) : ''}
            size="large"
          />
        </AvatarContainer>
        {user ? (
          <div>
            <Name data-cy="memberName">{getUserLabel(user) || '-'}</Name>
            {user?.role && <MemberRole>{user.role.roleName}</MemberRole>}
          </div>
        ) : null}
      </MemberRow>
    );
  }, [user]);

  return (
    <Modal
      title={<FormattedMessage id="project.members.add_permission.label" />}
      okButtonProps={{
        loading: submitInProgress,
      }}
      cancelButtonProps={{
        type: 'text',
      }}
      cancelText={<FormattedMessage id="no" />}
      okText={<FormattedMessage id="project.members.set_permission.label" />}
      onOk={onOk}
      onCancel={onClose}
      visible
      confirmLoading={isLoading}
    >
      <Wrapper>
        {isLoading ? (
          <StyledRow>
            <Loading />
          </StyledRow>
        ) : (
          <>
            {memberDetails}
            <Form layout="vertical">
              <Form.Item required>
                <Select
                  options={options}
                  value={selectedPermission}
                  onChange={onPermissionChange}
                  placeholder={
                    <FormattedMessage id="project.members.choose_project_permissions.placeholder" />
                  }
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Wrapper>
    </Modal>
  );
};
